import api from './axiosConfig';

/**
 * Get all customers with pagination and filters
 * @param {Object} params - Query parameters (page, limit, etc.)
 * @returns {Promise<Object>} - Customers data and pagination info
 */
export const getCustomers = async (params = {}) => {
  try {
    const response = await api.get('/customers', { 
      params,
      authenticated: true 
    });
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || 'Failed to get customers');
    }
    throw new Error('Network error. Please try again.');
  }
};

/**
 * Get customer by ID
 * @param {string} id - Customer ID
 * @returns {Promise<Object>} - Customer data
 */
export const getCustomerById = async (id) => {
  try {
    const response = await api.get(`/customers/${id}`, { authenticated: true });
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      throw new Error('Customer not found');
    }
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || 'Failed to get customer');
    }
    throw new Error('Network error. Please try again.');
  }
};

/**
 * Create new customer
 * @param {Object} customerData - Customer data
 * @returns {Promise<Object>} - Created customer data
 */
export const createCustomer = async (customerData) => {
  try {
    const response = await api.post('/customers', customerData, { authenticated: true });
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || 'Failed to create customer');
    }
    throw new Error('Network error. Please try again.');
  }
};

/**
 * Update customer
 * @param {string} id - Customer ID
 * @param {Object} customerData - Updated customer data
 * @returns {Promise<Object>} - Updated customer data
 */
export const updateCustomer = async (id, customerData) => {
  try {
    const response = await api.put(`/customers/${id}`, customerData, { authenticated: true });
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      throw new Error('Customer not found');
    }
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || 'Failed to update customer');
    }
    throw new Error('Network error. Please try again.');
  }
};

/**
 * Delete customer
 * @param {string} id - Customer ID
 * @returns {Promise<Object>} - Success message
 */
export const deleteCustomer = async (id) => {
  try {
    const response = await api.delete(`/customers/${id}`, { authenticated: true });
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      throw new Error('Customer not found');
    }
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || 'Failed to delete customer');
    }
    throw new Error('Network error. Please try again.');
  }
};

/**
 * Export customers to CSV
 * @param {Object} params - Query parameters for filtering (country, province, state, search)
 * @returns {Promise<Blob>} - CSV file as a blob
 */
export const exportCustomers = async (params = {}) => {
  try {
    const response = await api.get('/customers/export', { 
      params,
      responseType: 'blob',
      authenticated: true 
    });
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      // Handle blob error
      const text = await new Response(error.response.data).text();
      let message;
      try {
        const json = JSON.parse(text);
        message = json.message || 'Failed to export customers';
      } catch (e) {
        message = 'Failed to export customers';
      }
      throw new Error(message);
    }
    throw new Error('Network error. Please try again.');
  }
};